import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  ArrowRight, Clock, Target, ShieldAlert, ShoppingCart,
  Search, Filter, AlertTriangle, TrendingUp, Timer
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function TimeAgo({ date }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(i);
  }, []);
  if (!date) return null;
  const diff = Math.floor((now - new Date(date).getTime()) / 1000);
  if (diff < 60) return <span>{diff}s ago</span>;
  if (diff < 3600) return <span>{Math.floor(diff/60)}m ago</span>;
  if (diff < 86400) return <span>{Math.floor(diff/3600)}h ago</span>;
  return <span>{Math.floor(diff/86400)}d ago</span>;
}

export default function History() {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('ALL');
  const [successFilter, setSuccessFilter] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchHistory = useCallback(async () => {
    try {
      const statusParam = filter !== 'all' ? `&status=${filter}` : '';
      const res = await fetch(`${API}/api/recommendations/all?limit=100${statusParam}`);
      if (res.ok) {
        const data = await res.json();
        setRecommendations(data.recommendations || []);
        setTotal(data.total || 0);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    fetchHistory();
  }, [fetchHistory]);

  const filtered = recommendations.filter(rec => {
    if (searchTerm && !rec.symbol?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !rec.company_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (riskFilter !== 'ALL' && rec.risk_level !== riskFilter) return false;
    if (successFilter > 0 && (rec.confidence || 0) < successFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full rounded-xl bg-[hsl(var(--surface-2))]" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl bg-[hsl(var(--surface-2))]" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in" data-testid="history-page">
      <div>
        <h1 className="text-2xl font-bold mb-1">Recommendation History</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Browse all past and current recommendations</p>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
        <Tabs value={filter} onValueChange={setFilter} data-testid="history-status-tabs">
          <TabsList className="bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
            <TabsTrigger value="all" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">All ({total})</TabsTrigger>
            <TabsTrigger value="active" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">Active</TabsTrigger>
            <TabsTrigger value="bought" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">Bought</TabsTrigger>
            <TabsTrigger value="expired" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">Expired</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
            <input type="text" placeholder="Search ticker..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))] w-36"
              data-testid="history-ticker-search" />
          </div>
          <select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)}
            className="text-xs px-3 py-1.5 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]" data-testid="history-risk-filter">
            <option value="ALL">All Risk</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
          </select>
          <select value={successFilter} onChange={(e) => setSuccessFilter(Number(e.target.value))}
            className="text-xs px-3 py-1.5 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]" data-testid="history-success-filter">
            <option value={0}>All Success %</option>
            <option value={50}>50%+</option>
            <option value={60}>60%+</option>
            <option value={70}>70%+</option>
            <option value={80}>80%+</option>
          </select>
        </div>
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="text-center py-10 text-[hsl(var(--muted-foreground))]">
          <Filter className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-base font-medium">No recommendations found</p>
          <p className="text-sm">Adjust filters or run a new analysis</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((rec) => {
            const estReturn = rec.estimated_return_pct || (rec.buy_price > 0 ? ((rec.target_price - rec.buy_price) / rec.buy_price * 100).toFixed(1) : 0);
            const isExpired = rec.status === 'expired';
            const isBought = rec.status === 'bought';
            return (
              <div key={rec._id}
                onClick={() => navigate(`/recommendation/${rec._id}`)}
                className={`p-4 rounded-xl border bg-[hsl(var(--card))] cursor-pointer hover:border-[hsl(var(--hairline))] hover:shadow-[0_10px_30px_-15px_hsl(var(--shadow-raise))] transition-all duration-200 ${
                  isExpired ? 'border-[hsl(var(--border))] opacity-70' :
                  isBought ? 'border-emerald-500/30' :
                  'border-[hsl(var(--primary))]/20'
                }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-base font-bold">{rec.symbol}</span>
                    <Badge variant="outline" className={`text-[10px] uppercase ${
                      rec.status === 'active' ? 'text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30' :
                      isBought ? 'text-emerald-400 border-emerald-500/30' :
                      'text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]'
                    }`}>{rec.status}</Badge>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                    <Timer className="w-3 h-3" />
                    <TimeAgo date={rec.created_at} />
                  </div>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2 truncate">{rec.company_name}</p>
                <div className="flex items-center gap-2 text-xs font-mono tabular-nums mb-2">
                  <span>${rec.buy_price?.toFixed(2)}</span>
                  <ArrowRight className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />
                  <span className="text-emerald-400">${rec.target_price?.toFixed(2)}</span>
                  <span className="text-red-400 ml-auto">S: ${rec.stop_loss?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${(rec.confidence||0) >= 70 ? 'bg-emerald-400' : (rec.confidence||0) >= 50 ? 'bg-[hsl(var(--primary))]' : 'bg-red-400'}`} />
                    <span className="text-[10px] font-mono">{rec.confidence||0}%</span>
                  </div>
                  <Badge variant="outline" className={`text-[9px] ${
                    rec.risk_level === 'LOW' ? 'text-emerald-400 border-emerald-500/30' :
                    rec.risk_level === 'HIGH' ? 'text-red-400 border-red-500/30' :
                    'text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30'
                  }`}>{rec.risk_level}</Badge>
                  <span className="font-mono text-xs font-bold text-emerald-400">+{Number(estReturn).toFixed(1)}%</span>
                </div>
                {isExpired && rec.expired_at && (
                  <p className="text-[10px] text-red-400/60 mt-2">Expired: {new Date(rec.expired_at).toLocaleString()}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
